import { createElement } from 'react';

import type { useDialog } from '../../components/Dialog.tsx';
import { checkName, type ConflictPolicy } from '../../lib/queries.ts';
import { UploadConflict } from './UploadConflict.tsx';

/**
 * Ask about a name before making the thing that would collide with it.
 *
 * An upload has asked this question for months — keep both, replace, or don't
 * — and creating a document or a sheet from the New menu did not. It sent the
 * name, the server refused it, and the user got a red box reading *A document
 * called "Untitled document" is already in that folder* with no way forward
 * except to guess a different name and try again. Which is not an answer; it
 * is the same refusal a file explorer stopped giving in about 1995.
 *
 * So the check runs first and, if the name is taken, the same three-answer
 * dialog the upload uses opens over it. What comes back is the policy to send
 * with the create, or null for "don't".
 */

export type Settled = { policy: ConflictPolicy } | null;

export async function settleName(
  dialog: ReturnType<typeof useDialog>,
  what: 'document' | 'sheet',
  name: string,
  folderId: string | null,
): Promise<Settled> {
  const check = await checkName(folderId, name, what).catch(() => null);

  /*
   * A check that could not be made is not a reason to refuse to create.
   * Falling through means the server may still reject the name, which is
   * exactly where this started — but only when the network is already
   * failing, rather than every time.
   */
  if (!check || !check.taken) return { policy: 'error' };

  return new Promise<Settled>((resolve) => {
    let answered = false;

    dialog.open((close) =>
      createElement(UploadConflict, {
        filename: name,
        check,
        action: 'create' as const,
        // Nothing is queued behind it: this is one document, not a batch of
        // uploads, so "apply to the rest" would be an offer about nothing.
        remaining: 0,
        onChoose: (choice: ConflictPolicy | null) => {
          answered = true;
          resolve(choice === null ? null : { policy: choice });
        },
        onClose: () => {
          close();
          // Dismissed with Escape or the backdrop, which is "don't".
          if (!answered) resolve(null);
        },
      }),
    );
  });
}
